import React, { useState, useEffect } from 'react';
import { User, Save } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PersonalInfo = ({ user, userProfile, onSave, saving }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: ''
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (userProfile) {
      setFormData({
        full_name: userProfile?.full_name || '',
        email: userProfile?.email || user?.email || ''
      });
    }
  }, [userProfile, user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.full_name?.trim()) {
      newErrors.full_name = 'Full name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  return (
    <div className="p-6">
      <div className="border-b border-gray-200 pb-4 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
          <User className="h-5 w-5 mr-2" />
          Personal Information
        </h2>
        <p className="text-gray-600 mt-1">
          Update your personal details and contact information
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Full Name
          </label>
          <Input
            type="text"
            value={formData?.full_name}
            onChange={(e) => handleChange('full_name', e?.target?.value)}
            placeholder="Enter your full name"
            error={errors?.full_name}
            className="max-w-md"
          />
          {errors?.full_name && (
            <p className="text-red-600 text-sm mt-1">{errors?.full_name}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="max-w-md">
            <Input
              type="email"
              value={formData?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              placeholder="Enter your email address"
              error={errors?.email}
            />
            {errors?.email && (
              <p className="text-red-600 text-sm mt-1">{errors?.email}</p>
            )}
            <p className="text-gray-500 text-sm mt-1">
              This email will be used for outage notifications and account communications
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Type
          </label>
          <div className="max-w-md">
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium capitalize ${
              userProfile?.role === 'admin' ? 'text-purple-800 bg-purple-100' :
              userProfile?.role === 'moderator'? 'text-blue-800 bg-blue-100' : 'text-green-800 bg-green-100'
            }`}>
              {userProfile?.role || 'user'}
            </span>
            <p className="text-gray-500 text-sm mt-1">
              Your account type determines your access level within the platform
            </p>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Status
          </label>
          <div className="flex items-center space-x-3">
            <span className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
              userProfile?.is_verified 
                ? 'text-green-800 bg-green-100' :'text-yellow-800 bg-yellow-100'
            }`}>
              {userProfile?.is_verified ? 'Verified' : 'Unverified'}
            </span>
            {!userProfile?.is_verified && (
              <Button variant="outline" size="sm">
                Verify Account
              </Button>
            )}
          </div>
          <p className="text-gray-500 text-sm mt-1">
            {userProfile?.is_verified 
              ? 'Your account has been verified'
              : 'Verify your account to access additional features'
            }
          </p>
        </div>

        <div className="pt-4 border-t border-gray-200">
          <Button
            type="submit"
            disabled={saving}
            className="flex items-center"
          >
            {saving ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default PersonalInfo;